import type { ChangeEvent } from 'react';
import { useResend } from '@auth0/auth0-acul-react/mfa-sms-challenge';
import SecurityCodeInput from 'creative-ui/oktaComponents/SecurityCodeInput';
import Cta from 'creative-ui/uikit/Cta';
import CtaContainer from 'creative-ui/uikit/CtaContainer';
import MiniControl from 'creative-ui/uikit/MiniControl';
import Modal from 'creative-ui/uikit/Modal';
import StatusMessage from 'creative-ui/uikit/StatusMessage';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import popinRenvoieNumeroActivationSvg from '@/assets/popin-renvoie-numero-activation.svg';
import { Flex } from '@/components/Layout/Flex/Flex';
import { Text } from '@/components/Typography/Text/Text';
import {
  MFA_OTP_CODE_LENGTH,
  MFA_RESEND_TIMEOUT_SECONDS,
} from '@/constants/mfa';
import { trackClick } from '@/lib/tagging';

import { useMfaSmsChallengeManager } from '../hooks/useMfaSmsChallengeManager';

const OTP_CODE_PATTERN = new RegExp(`^[0-9]{${MFA_OTP_CODE_LENGTH}}$`);

interface MfaSmsChallengeFormProps {
  fieldErrors: Record<string, string>;
}

interface FormData {
  code: string;
  rememberDevice: boolean;
}

function MfaSmsChallengeForm({
  fieldErrors,
}: Readonly<MfaSmsChallengeFormProps>) {
  const { t } = useTranslation('mfaSmsChallenge');
  const {
    handleContinueMfaSmsChallenge,
    handleEditIdentifier,
    handleTryAnotherMethod,
    canEditIdentifier,
    canSelectFactor,
    showRememberDevice,
    isLoginChallenge,
    phoneNumber,
    texts,
  } = useMfaSmsChallengeManager();

  const {
    remaining,
    disabled: isResendDisabled,
    startResend,
  } = useResend({
    timeoutSeconds: MFA_RESEND_TIMEOUT_SECONDS,
  });

  const [isResending, setIsResending] = useState(false);
  const [isResendModalOpen, setIsResendModalOpen] = useState(false);

  const form = useForm<FormData>({
    defaultValues: {
      code: '',
      rememberDevice: false,
    },
  });

  const {
    control,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = form;

  // SDK first, i18n fallback
  const modifyText = texts?.modifyText || t('form.modifyText');
  const codeLabel = texts?.placeholder || t('form.codeLabel');
  const buttonText = texts?.buttonText || t('form.verifyButtonText');
  const resendCodeText = texts?.resendActionText || t('form.resendCodeText');
  const rememberDeviceLabel =
    texts?.rememberMeText || t('form.rememberDeviceLabel');
  const tryAnotherMethodText =
    texts?.pickAuthenticatorText || t('footer.tryAnotherMethodText');

  // i18n only (validation/UX strings)
  const verifyingText = t('form.verifyingText');
  const didntGetCodeText = t('form.didntGetCodeText', {
    time: MFA_RESEND_TIMEOUT_SECONDS,
  });
  const maxAttemptsText = t('form.maxAttemptsText');
  const codeRequiredError = t('errors.codeRequired');
  const codeInvalidFormatError = t('errors.codeInvalidFormat');
  const resendFailedError = t('errors.resendFailed');
  const genericError = t('errors.generic');
  const resendModalTitle = t('resendModal.title');
  const resendModalSpamHint = t('resendModal.spamHint');

  // Get SDK errors for code field
  const codeSDKError = fieldErrors.code;

  const onSubmit = async (formData: FormData) => {
    try {
      await handleContinueMfaSmsChallenge(
        formData.code,
        formData.rememberDevice,
      );
    } catch {
      setError('root', {
        type: 'manual',
        message: genericError,
      });
    }
  };

  const onResendCode = async () => {
    trackClick(
      `${isLoginChallenge ? 'login/sms/code' : 'mfa/sms/verification'}/resend`,
    );
    setIsResending(true);
    try {
      startResend();
    } catch {
      setError('root', {
        type: 'manual',
        message: resendFailedError,
      });
    } finally {
      setIsResendModalOpen(true);
      setIsResending(false);
    }
  };

  const onTryAnotherMethod = () => {
    trackClick(
      `${isLoginChallenge ? 'login/sms/code' : 'mfa/sms/verification'}/try-another-method`,
    );
    handleTryAnotherMethod();
  };

  const codeErrorMessage =
    errors.code?.message || errors.root?.message || codeSDKError;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {canEditIdentifier && (
        <CtaContainer className="u-margin-form">
          <Cta type="ghost-inline" onClick={handleEditIdentifier}>
            {modifyText}
          </Cta>
        </CtaContainer>
      )}

      {/* OTP Code input */}
      <Controller
        name="code"
        control={control}
        rules={{
          required: codeRequiredError,
          pattern: {
            value: OTP_CODE_PATTERN,
            message: codeInvalidFormatError,
          },
        }}
        render={({ field }) => (
          <SecurityCodeInput
            className="u-margin-form"
            name={field.name}
            value={field.value}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              field.onChange(e.target.value)
            }
            maxLength={MFA_OTP_CODE_LENGTH}
            label={codeLabel}
          />
        )}
      />
      {codeErrorMessage && (
        <StatusMessage className="u-margin-form" mode="error">
          {codeErrorMessage}
        </StatusMessage>
      )}

      <Text size="sm" accent="soft" align="center">
        {didntGetCodeText}
      </Text>
      <CtaContainer stack>
        <Cta
          className="u-margin-b-s"
          type="ghost-inline"
          onClick={onResendCode}
          disabled={isResendDisabled || isResending}>
          {resendCodeText}
        </Cta>
        {canSelectFactor && (
          <Cta
            className="u-margin-b-s"
            type="ghost-inline"
            onClick={onTryAnotherMethod}>
            {tryAnotherMethodText}
          </Cta>
        )}
      </CtaContainer>

      {isResendDisabled && (
        <Text size="sm" accent="soft" align="center">
          {remaining > MFA_RESEND_TIMEOUT_SECONDS - 10
            ? t('form.codeSentText')
            : t('form.smsSentText', { time: remaining })}
        </Text>
      )}

      <Text size="sm" accent="soft" align="center" className="u-margin-form">
        {maxAttemptsText}
      </Text>

      {showRememberDevice && (
        <Controller
          name="rememberDevice"
          control={control}
          render={({ field }) => (
            <Flex justify="center" className="u-margin-form">
              <MiniControl
                name={field.name}
                checked={field.value}
                onChange={field.onChange}>
                {rememberDeviceLabel}
              </MiniControl>
            </Flex>
          )}
        />
      )}

      <CtaContainer stack className="u-margin-b-l">
        <Cta
          className="c-submitCta"
          type="primary"
          submit
          disabled={isSubmitting}
          isWaiting={isSubmitting}>
          {isSubmitting ? verifyingText : buttonText}
        </Cta>
      </CtaContainer>

      <Modal
        open={isResendModalOpen}
        onClickClose={() => setIsResendModalOpen(false)}
        modalName={resendModalTitle}>
        <Flex
          justify="center"
          direction="row"
          align="center"
          className="u-margin-b-l">
          <img src={popinRenvoieNumeroActivationSvg} alt="numero activation" />
        </Flex>
        <div className="c-wysiwyg u-margin-b-l">
          <Text align="center" size="sm">
            {t('resendModal.message')}
          </Text>
          <Text align="center" bold>
            {phoneNumber}
          </Text>
        </div>
        <Text size="sm" accent="soft" align="center">
          {resendModalSpamHint}
        </Text>
      </Modal>
    </form>
  );
}

export default MfaSmsChallengeForm;
